import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { GoonStats, User } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { AuthedPage } from "../components/AuthedPage";
import { hintTitle } from "../components/hintTitle";
import { Button, Toggle } from "../ui/primitives";
import { Avatar } from "../ui/Avatar";
import { IconClose } from "../ui/icons";
import { ProfileBody, ShareCardButton } from "../profile/ProfileBody";
import { MASK } from "../../lib/replayMask";

/**
 * `/profile` — the signed-in owner's own page.
 *
 * The numbers and the layout all live in ProfileBody, which /u/:username renders too.
 * What is owner-only lands here, as the `ownerActions` slot: the sign-in wall, the
 * authenticated stats fetch, the share card (button AND modal — /u/:username removes it
 * by not passing it, never by a flag), and the switch that decides whether anyone else
 * may see any of it.
 */
export function Profile() {
  return (
    <AuthedPage title="Profile">
      <ProfileContent />
    </AuthedPage>
  );
}

function ProfileContent() {
  const user = useSession((s) => s.user);
  const refresh = useSession((s) => s.refresh);
  const [stats, setStats] = useState<GoonStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Bumped when the owner prunes a session: the tiles read the table live, so a refetch
  // is the whole update. Not a local subtraction — the server's numbers are the truth.
  const [statsVersion, setStatsVersion] = useState(0);
  const [avatarOpen, setAvatarOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    userApi
      .getStats()
      .then((s) => !cancelled && setStats(s))
      .catch(
        (e: unknown) =>
          !cancelled && setError(e instanceof Error ? e.message : String(e)),
      );
    return () => {
      cancelled = true;
    };
  }, [user, statsVersion]);

  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!stats || !user) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  // `!== false`, not `=== true`, and the asymmetry is the point. mapUser always sends
  // this, so undefined means only a session cached from before the column existed — but
  // of the two ways to be wrong about it, showing OFF while the page is actually PUBLIC
  // is the one that hurts: the owner would believe they are private while strangers read
  // their numbers. Showing ON while actually private only over-warns them.
  const isPublic = user.profilePublic !== false;

  const setPublic = (next: boolean) => {
    setSaving(true);
    userApi
      .setProfilePublic(next)
      // The session is what this toggle renders from, so the server's reply is what
      // makes the switch move — no optimistic flip that could disagree with the truth.
      .then((r) => refresh(r.user))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false));
  };

  return (
    <div className="space-y-6">
      {avatarOpen ? <AvatarDialog user={user} onClose={() => setAvatarOpen(false)} /> : null}
      <ProfileBody
        username={user.username}
        gold={(user.goldUntil ?? 0) > Date.now()}
        avatarUrl={user.avatarUrl}
        onEditAvatar={() => setAvatarOpen(true)}
        stats={stats}
        isOwner
        onStatsChanged={() => setStatsVersion((v) => v + 1)}
        ownerActions={
          <>
            <PublicProfileToggle
              username={user.username}
              isPublic={isPublic}
              saving={saving}
              onChange={setPublic}
            />
            <ShareCardButton
              username={user.username}
              stats={stats}
              gold={(user.goldUntil ?? 0) > Date.now()}
              profilePublic={isPublic}
            />
          </>
        }
      />
    </div>
  );
}

/**
 * The public-profile switch. Its title spells out the owner's own public URL —
 * `goonlords.com/u/<name>` — and /profile is a recorded route, so the switch carries the
 * replay marker: maskAttributeFn masks a title only on or inside a marker, and the URL
 * sits in plain title text where the global /u/ href rewrite does not reach. The cost is
 * that the switch's own "Public profile" label is masked with it.
 *
 * Exported for tools/checks/replay-mask.mts, which renders it with a sentinel username:
 * the name reaches the title through hintTitle and a template string, a path the static
 * ratchet cannot follow.
 */
export function PublicProfileToggle({
  username,
  isPublic,
  saving,
  onChange,
}: {
  username: string;
  isPublic: boolean;
  saving: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="w-56" {...MASK}>
      <Toggle
        checked={isPublic}
        onChange={onChange}
        disabled={saving}
        label="Public profile"
        title={hintTitle(
          "Public profile",
          // Toggle asks a caller that disables it to say why in the title.
          saving
            ? "Saving…"
            : isPublic
              ? `Anyone can open goonlords.com/u/${username} and see these stats.`
              : "Only you can see these stats. Your page shows your name and collections.",
        )}
      />
    </div>
  );
}

/**
 * The picture editor (2026-09-13): pick an image, see the square the server will keep
 * (the preview crops the way the cut does — centred cover), save, or take the picture
 * off. The saved User comes back with `avatarUrl` and goes straight into the session, so
 * every surface that reads the session repaints without a second fetch.
 */
function AvatarDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const refresh = useSession((s) => s.refresh);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const save = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await userApi.avatarUpload(file);
      refresh(r.user);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "That didn't save.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await userApi.avatarRemove();
      refresh(r.user);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "That didn't save.");
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-100">Your picture</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-white">
            <IconClose />
          </button>
        </div>
        <div className="mb-4 flex items-center gap-4">
          <Avatar name={user.username} src={preview ?? user.avatarUrl} size="lg" className="!h-24 !w-24 !text-3xl" />
          <div className="min-w-0 flex-1 text-xs text-zinc-400">
            Shown beside your name everywhere — the Feed, the boards, your page. A square is
            cut from the middle of what you pick.
          </div>
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          disabled={busy}
          onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
          className="block w-full text-xs text-zinc-400 file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--color-accent)] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-black hover:file:opacity-90"
        />
        <p className="mt-1 text-[11px] text-zinc-500">JPG, PNG, WebP or GIF (a GIF keeps looping), under 8 MB.</p>
        <div className="mt-4 flex items-center gap-2">
          <Button variant="primary" disabled={!file || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {user.avatarUrl ? (
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Remove picture
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
        </div>
        {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
      </div>
    </div>,
    document.body,
  );
}
